/**
 * CENTRAL BUSINESS CONFIGURATION
 * ------------------------------------------------------------------
 * This is the single source of truth for every business fact, contact
 * detail and legal claim used across the site. Update values here —
 * do not hardcode phone numbers, addresses, or claims inside components
 * or pages.
 *
 * Any field marked with a "TO CONFIRM" value, or with `confirmed: false`,
 * is a clearly labelled placeholder. Components must treat these as
 * not-yet-public information (e.g. hide a physical address, avoid
 * rendering an unconfirmed claim as fact) rather than displaying the
 * placeholder text to site visitors as if it were real.
 * ------------------------------------------------------------------
 */

export const businessConfig = {
  identity: {
    tradingName: "Frazzetto Concrete Cutting & Coring",
    shortName: "Frazzetto",
    legalEntityName: "Star Picket Pipe Global Pty Ltd",
    abn: "74 644 131 911",
    legalEntityConfirmed: true,
  },

  people: {
    principal: {
      name: "Salvatore Frazzetto",
      yearsOfIndustryExperience: 25,
      // Important: this experience figure belongs to Salvatore personally,
      // not to the age of the Frazzetto business, which is newly launched.
      experienceDisclaimer:
        "Refers to Salvatore Frazzetto's personal experience working in the concrete-cutting industry, not the age of the Frazzetto business.",
    },
  },

  contact: {
    phoneDisplay: "0432 068 695",
    phoneHref: "tel:+61432068695",
    phoneConfirmed: true,
    email: "quotes@frazzettoconcretecutting.com.au",
    emailConfirmed: true,
    // Address visibility: the business does not currently display a public
    // office address. Set showAddress to true only once confirmed this is
    // desired and accurate.
    showAddress: false,
    address: {
      line1: "TO CONFIRM",
      suburb: "TO CONFIRM",
      state: "VIC",
      postcode: "TO CONFIRM",
    },
    preferredContactMethods: ["Phone", "Email", "Quote form"],
  },

  serviceRegion: {
    primaryLabel: "Melbourne & Victoria",
    summary:
      "Based in Melbourne, covering the metropolitan area and surrounding regions, with work further across Victoria considered by arrangement depending on the job.",
    confirmed: true,
  },

  hours: {
    display: "Mon–Sat 7am–5pm · Sun 9am–5pm",
    confirmed: true,
  },

  legalCompliance: {
    // Public liability insurance, confirmed by the business as in place.
    // NOTE: ensure the policy is genuinely active before go-live.
    publicLiabilityInsurance: {
      status: "Public liability insured",
      confirmed: true,
    },
    // Safe Work Method Statements provided for relevant higher-risk work.
    swms: {
      status: "Site-specific SWMS provided for relevant higher-risk work",
      confirmed: true,
    },
  },

  online: {
    domain: "frazzettoconcretecutting.com.au",
    googleBusinessProfileUrl:
      "https://www.google.com/search?q=Frazzetto+Concrete+Cutting+%26+Coring",
    social: {
      facebook: "https://www.facebook.com/profile.php?id=61592242694037",
      instagram: "https://www.instagram.com/frazzettoconcretecutting/",
    },
  },

  reviews: {
    // Google reviews integration. The site NEVER fabricates reviews or star
    // ratings. Until real reviews exist, the Reviews section shows an honest
    // "be the first to review us" state (see src/components/sections/Reviews.tsx).
    //
    // To surface real Google reviews you have two options after launch:
    //  1. Manual: add verified reviews to src/data/reviews.ts (author, rating,
    //     text, date). Simple, no API, but you maintain them by hand.
    //  2. Live widget/API: once the Google Business Profile is verified, embed
    //     a Google reviews widget using the Place ID below.
    googlePlaceId: "TO CONFIRM",
    // Direct "write a review" link, replace PLACE_ID once the Google
    // Business Profile Place ID is known:
    // https://search.google.com/local/writereview?placeid=PLACE_ID
    writeReviewUrl: "TO CONFIRM",
  },

  quoteForm: {
    // Live submission is handled by Web3Forms, which forwards submissions
    // straight to the business inbox with no backend to host. Set the access
    // key in VITE_WEB3FORMS_KEY (see .env.example). Until a key is present,
    // the form falls back to opening the visitor's email client (mailto) so
    // it still works. See src/components/forms/QuoteForm.tsx.
    accessKeyEnvVar: "VITE_WEB3FORMS_KEY",
    maxFileSizeMb: 15,
    acceptedFileTypes: [".jpg", ".jpeg", ".png", ".heic", ".pdf"],
  },

  // Feature flags that keep unfinished/empty sections out of navigation
  // and off the homepage until real content exists, per project rules.
  features: {
    showProjectsPage: false, // enable once genuine project photography/case studies exist
    showResourcesSection: false, // enable once real guides/articles exist
  },

  seo: {
    siteName: "Frazzetto Concrete Cutting & Coring",
    defaultTitleSuffix: "Frazzetto Concrete Cutting & Coring",
    defaultDescription:
      "Professional concrete cutting, core drilling and controlled concrete removal for homeowners, trades and commercial projects across Melbourne and Victoria.",
    // Used for canonical URL + Open Graph construction.
    baseUrl: "https://frazzettoconcretecutting.com.au",
    ogImage: "/og-image.jpg",
  },
} as const;

export type BusinessConfig = typeof businessConfig;
